import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SanitationEquipment, SanitationRecord } from '../../types/sanitationRecord';
import sanitationApiService from '../../services/sanitationApiService';

interface SanitationState {
  equipment: SanitationEquipment[];
  records: SanitationRecord[];
  loading: boolean;
  error: string | null;
}

const initialState: SanitationState = {
  equipment: [],
  records: [],
  loading: false,
  error: null,
};

// Equipment thunks
export const fetchSanitationEquipment = createAsyncThunk(
  'sanitation/fetchEquipment',
  async () => {
    const equipment = await sanitationApiService.fetchEquipment();
    return equipment;
  }
);

export const createSanitationEquipment = createAsyncThunk(
  'sanitation/createEquipment',
  async (equipment: Partial<SanitationEquipment>) => {
    const created = await sanitationApiService.createEquipment(equipment);
    return created;
  }
);

export const updateSanitationEquipment = createAsyncThunk(
  'sanitation/updateEquipment',
  async ({ id, equipment }: { id: string; equipment: Partial<SanitationEquipment> }) => {
    const updated = await sanitationApiService.updateEquipment(id, equipment);
    return updated;
  }
);

export const deleteSanitationEquipment = createAsyncThunk(
  'sanitation/deleteEquipment',
  async (id: string) => {
    await sanitationApiService.deleteEquipment(id);
    return id;
  }
);

// Record thunks
export const fetchSanitationRecords = createAsyncThunk(
  'sanitation/fetchRecords',
  async (equipmentId?: string) => {
    const records = await sanitationApiService.fetchRecords(equipmentId);
    return { equipmentId, records };
  }
);

export const createSanitationRecord = createAsyncThunk(
  'sanitation/createRecord',
  async (record: Partial<SanitationRecord>) => {
    const created = await sanitationApiService.createRecord(record);
    return created;
  }
);

export const updateSanitationRecord = createAsyncThunk(
  'sanitation/updateRecord',
  async ({ id, record }: { id: string; record: Partial<SanitationRecord> }) => {
    const updated = await sanitationApiService.updateRecord(id, record);
    return updated;
  }
);

export const deleteSanitationRecord = createAsyncThunk(
  'sanitation/deleteRecord',
  async (id: string) => {
    await sanitationApiService.deleteRecord(id);
    return id;
  }
);

const sanitationSlice = createSlice({
  name: 'sanitation',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch equipment
    builder
      .addCase(fetchSanitationEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSanitationEquipment.fulfilled, (state, action: PayloadAction<SanitationEquipment[]>) => {
        state.loading = false;
        state.equipment = action.payload;
      })
      .addCase(fetchSanitationEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch sanitation equipment';
      });

    // Create equipment
    builder
      .addCase(createSanitationEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSanitationEquipment.fulfilled, (state, action: PayloadAction<SanitationEquipment>) => {
        state.loading = false;
        state.equipment.push(action.payload);
      })
      .addCase(createSanitationEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create sanitation equipment';
      });

    // Update equipment
    builder
      .addCase(updateSanitationEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSanitationEquipment.fulfilled, (state, action: PayloadAction<SanitationEquipment>) => {
        state.loading = false;
        const index = state.equipment.findIndex((e) => e.id === action.payload.id);
        if (index !== -1) {
          state.equipment[index] = action.payload;
        }
      })
      .addCase(updateSanitationEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update sanitation equipment';
      });

    // Delete equipment
    builder
      .addCase(deleteSanitationEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSanitationEquipment.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.equipment = state.equipment.filter((e) => e.id !== action.payload);
      })
      .addCase(deleteSanitationEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete sanitation equipment';
      });

    // Fetch records
    builder
      .addCase(fetchSanitationRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSanitationRecords.fulfilled, (state, action: PayloadAction<{ equipmentId?: string; records: SanitationRecord[] }>) => {
        state.loading = false;
        if (action.payload.equipmentId) {
          // Merge records for specific equipment
          const otherRecords = state.records.filter(r => r.equipmentId !== action.payload.equipmentId);
          state.records = [...otherRecords, ...action.payload.records];
        } else {
          state.records = action.payload.records;
        }
      })
      .addCase(fetchSanitationRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch sanitation records';
      });

    // Create record
    builder
      .addCase(createSanitationRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSanitationRecord.fulfilled, (state, action: PayloadAction<SanitationRecord>) => {
        state.loading = false;
        state.records.unshift(action.payload);
      })
      .addCase(createSanitationRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create sanitation record';
      });

    // Update record
    builder
      .addCase(updateSanitationRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSanitationRecord.fulfilled, (state, action: PayloadAction<SanitationRecord>) => {
        state.loading = false;
        const index = state.records.findIndex((r) => r.id === action.payload.id);
        if (index !== -1) {
          state.records[index] = action.payload;
        }
      })
      .addCase(updateSanitationRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update sanitation record';
      });

    // Delete record
    builder
      .addCase(deleteSanitationRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSanitationRecord.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.records = state.records.filter((r) => r.id !== action.payload);
      })
      .addCase(deleteSanitationRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete sanitation record';
      });
  },
});

export const { clearError } = sanitationSlice.actions;
export default sanitationSlice.reducer;
